// How the six resource kinds are presented to a person.
//
// The server has six kinds; the sidebar has five entries, because People,
// Subcontractors, Suppliers and Other are one kind each while **Assets is
// plant AND vehicle together**. That is the Zanetto Tools shape and it matches
// how someone actually thinks: "the fleet" is one list, and whether a
// particular machine happens to be road-registered is a property of the row,
// not a reason to look somewhere else.
//
// One definition, read by the Resources landing page, the list page, the
// sidebar group and the phone More list — so those four can never disagree
// about which kinds live where.
import type { ResourceKind } from '../schedulingApi';

export type ResourceGroupKey = 'people' | 'assets' | 'subcontractors' | 'suppliers' | 'other';

export interface ResourceGroup {
  key: ResourceGroupKey;
  /** The URL segment under /app/resources. */
  path: string;
  label: string;
  /** One line on the landing card, in the customer's language. */
  blurb: string;
  kinds: ResourceKind[];
  /** The kind a new row gets by default when created from this group. */
  defaultKind: ResourceKind;
}

export const RESOURCE_GROUPS: ResourceGroup[] = [
  {
    key: 'people',
    path: 'people',
    label: 'People',
    blurb: 'Your crew. Link them to an account and they see their own work.',
    kinds: ['person'],
    defaultKind: 'person',
  },
  {
    key: 'assets',
    path: 'assets',
    label: 'Assets',
    blurb: 'Plant and vehicles, the things with regos, meters and services.',
    kinds: ['plant', 'vehicle'],
    defaultKind: 'plant',
  },
  {
    key: 'subcontractors',
    path: 'subcontractors',
    label: 'Subcontractors',
    blurb: 'Booked onto jobs like crew, without an account.',
    kinds: ['subcontractor'],
    defaultKind: 'subcontractor',
  },
  {
    key: 'suppliers',
    path: 'suppliers',
    label: 'Suppliers',
    // Said plainly on the card, because the first thing someone tries is to
    // book one, and the server refusing it is a worse way to learn.
    blurb: 'Who services your gear. Suppliers are not scheduled onto jobs.',
    kinds: ['supplier'],
    defaultKind: 'supplier',
  },
  {
    key: 'other',
    path: 'other',
    label: 'Other',
    blurb: 'Anything else you book that is none of the above.',
    kinds: ['other'],
    defaultKind: 'other',
  },
];

export const KIND_LABEL: Record<ResourceKind, string> = {
  person: 'Person',
  plant: 'Plant',
  vehicle: 'Vehicle',
  subcontractor: 'Subcontractor',
  supplier: 'Supplier',
  other: 'Other',
};

/**
 * A resource's display label. Mirrors `resourceLabel` in
 * server/src/lib/resourceTypes.ts, and carries the same rule: the identifier
 * is optional, so every label degrades to the name alone rather than to a gap.
 *
 * Duplicated rather than shared because the two sides of this app do not share
 * a module — the server already sends `label` on a Resource, and this exists
 * for the places that hold a name and an identifier without a whole resource
 * (a meter row joined to its machine, for one).
 */
export function resourceLabel(resource: { name: string; identifier?: string | null }): string {
  const id = resource.identifier?.trim();
  return id ? `${resource.name} (${id})` : resource.name;
}

export function groupByKey(key: ResourceGroupKey): ResourceGroup {
  const group = RESOURCE_GROUPS.find((g) => g.key === key);
  if (!group) throw new Error(`Unknown resource group: ${key}`);
  return group;
}

/** Which group a resource belongs in — used to link a detail page back. */
export function groupForKind(kind: ResourceKind): ResourceGroup {
  return RESOURCE_GROUPS.find((g) => g.kinds.includes(kind)) ?? groupByKey('other');
}

/**
 * Which kinds read as PEOPLE on a booking card, as opposed to machines.
 *
 * The agenda card splits a job's resources into "Crew" and "Plant" — Zanetto
 * Tools' shape, ported in the 2026-08-31 agenda pass. This is deliberately a
 * list of the people kinds with everything else falling to plant, rather than
 * two lists: a seventh kind added later should land under Plant, which is a
 * visible mistake on the card, instead of vanishing from it entirely.
 *
 * It is separate from the `people` GROUP above even though it looks like it
 * overlaps: that group is one sidebar entry (`person` only, because
 * subcontractors get their own list), while this is the question "is this a
 * person or a machine" — the same not-quite-coincidence the server's three
 * identical kind lists are kept apart for.
 */
export const CREW_KINDS: ResourceKind[] = ['person', 'subcontractor'];

export function isCrewKind(kind: ResourceKind): boolean {
  return CREW_KINDS.includes(kind);
}
